import { Card, CardContent, CardHeader, InitialsAvatar, Spinner } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { TbUser } from "react-icons/tb";

import { ProfileInfo } from "@/pages/profile/components/profile-info";
import { useMe } from "@/queries/user.queries";
import { fadeInUp, transitions } from "@/utils/animations";

function ProfilePage() {
    const { t } = useTranslation();
    const { data: user, isLoading: isLoadingUser, error: userError } = useMe();

    const hasUserError = userError || !user;

    if (isLoadingUser) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
                <div className="flex justify-center items-center min-h-[60vh] bg-background">
                    <Card className="w-full max-w-md flex items-center justify-center min-h-[300px]">
                        <Spinner size="lg" />
                    </Card>
                </div>
            </div>
        );
    }

    if (hasUserError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
                <div className="flex justify-center items-center min-h-[60vh] bg-background">
                    <Card className="w-full max-w-md flex items-center justify-center min-h-[300px]">
                        <div className="text-destructive text-center w-full">{t("error")}</div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
            <div className="container mx-auto p-2 max-w-6xl overflow-auto">
                <motion.div {...fadeInUp} transition={transitions.slow} className="space-y-8">
                    {/* Hero Section */}
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/5 via-primary/3 to-background border border-primary/10 shadow-sm">
                        <div className="relative p-8">
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                <div className="relative">
                                    <InitialsAvatar
                                        name={user.name}
                                        size="lg"
                                        className="w-24 h-24 border-4 shadow-xl"
                                    />
                                </div>
                                <div className="flex-1 text-center md:text-left space-y-4">
                                    <div>
                                        <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            {user.name}
                                        </h1>
                                        <p className="text-xl text-muted-foreground mt-2">{user.email}</p>
                                    </div>
                                    <div className="flex flex-wrap gap-3 justify-center md:justify-start"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Personal Information */}
                            <Card className="border-border/40 shadow-sm">
                                <CardHeader className="pb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-primary/10">
                                            <TbUser className="w-5 h-5 text-primary" />
                                        </div>
                                        <h2 className="text-2xl font-semibold">{t("personal_information")}</h2>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <ProfileInfo user={user} />
                                </CardContent>
                            </Card>
                        </div>

                        {/* Aside */}
                        <aside className="space-y-8">
                            {/* Account Info */}
                            <Card className="border-border/40 shadow-sm">
                                <CardHeader className="pb-4">
                                    <h3 className="text-lg font-semibold">{t("account_information")}</h3>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">{t("member_since")}</span>
                                        <span className="text-sm font-medium">
                                            {DateTime.fromISO(user.createdAt).toLocaleString(DateTime.DATE_MED)}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </aside>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/profile")({
    component: ProfilePage,
});
